/* eslint-disable react-refresh/only-export-components */
import { useAccount, useMsal } from '@azure/msal-react'
import { Button } from '@fluentui/react-components'
import {
  HttpTransportType,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router'

import { acquireAccessToken } from '../config/msalInstanceHelper'
import { queryClient } from '../config/query-client'
import { GenerationStatus } from '../shared/enums/GenerationStatus'
import { StoryboardStep } from '../shared/enums/StoryboardStep'
import {
  type SceneElementGeneration,
  type ShotGeneration,
  type UpscaleGeneration,
  type UserImageGeneration,
} from '../shared/models/AIGeneration'
import { playgroundKeys } from '../shared/queries/usePlaygroundImages'
import { showToast } from '../utils/fluentToast'

import { handleImageStyleNotification } from './imageStyleNotifications'
import {
  handlePlaygroundNotification,
  handlePlaygroundVideoNotification,
} from './playgroundNotifications'

interface SignalRContextType {
  connection: HubConnection | null
  isConnected: boolean
  isConnecting: boolean
  connectionState: string
  reconnect: () => Promise<void>
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined)
const notifiedVideoActivities = new Set<string>()

/**
 * SignalR Provider — invalidates TanStack Query cache on real-time messages.
 *
 * When a SignalR message arrives:
 * 1. Invalidate the relevant TanStack Query cache → automatic refetch
 * 2. Show a toast notification
 */
export function SignalRProvider({ children }: { children: ReactNode }) {
  const connectionRef = useRef<HubConnection | null>(null)
  const [connection, setConnection] = useState<HubConnection | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionState, setConnectionState] = useState('Disconnected')
  const [authFailureCount, setAuthFailureCount] = useState(0)
  const [lastAuthFailure, setLastAuthFailure] = useState(0)
  const [isAuthenticationError, setIsAuthenticationError] = useState(false)

  const { instance } = useMsal()
  const accounts = instance.getAllAccounts()
  const account = useAccount(accounts[0] || null)
  const navigate = useNavigate()

  // ── Message handlers: invalidate queries + show toasts ──────────────

  const handleGenerationMessage = useCallback(
    (message: string) => {
      console.log('[SignalR] Received GenerationMessage:', message)
      try {
        const result: SceneElementGeneration = JSON.parse(message) as SceneElementGeneration

        // Update gen-status cache with the final status so even inactive queries reflect completion
        queryClient.setQueryData(
          ['scene-elements', 'gen-status', result.SceneElementId, result.VariantId],
          GenerationStatus[result.Status],
        )

        // Invalidate relevant queries — components auto-refetch
        queryClient.invalidateQueries({ queryKey: ['scene-elements', result.CommercialId] })
        queryClient.invalidateQueries({
          queryKey: ['scene-elements', 'detail', result.SceneElementId],
        })
        queryClient.invalidateQueries({
          queryKey: ['scene-elements', 'variant', result.SceneElementId, result.VariantId],
        })
        queryClient.invalidateQueries({
          queryKey: ['scene-elements', 'gen-status', result.SceneElementId, result.VariantId],
        })
        queryClient.invalidateQueries({ queryKey: ['queue'] })

        if (result.Status === GenerationStatus.Completed) {
          const onCommercialPage =
            location.pathname.startsWith('/commercial') &&
            location.pathname.includes(result.CommercialId)

          if (onCommercialPage) {
            showToast(
              `Generation completed for ${result.SceneElementName || 'scene element'}`,
              'success',
            )
          } else {
            showToast(
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span>Generation completed for scene element in commercial</span>
                <Button
                  size="small"
                  shape="circular"
                  onClick={() =>
                    navigate(`/storyboard/commercial/${result.CommercialId}`, {
                      state: { keepStep: true, targetStep: StoryboardStep.Script },
                    })
                  }
                >
                  View result
                </Button>
              </div>,
              'success',
            )
          }
        } else {
          showToast(`Error generating image for scene element`, 'error')
        }
      } catch (error) {
        console.error('Error processing generation result:', error)
      }
    },
    [navigate],
  )

  const handleScoutGenerationMessage = useCallback(
    (message: string) => {
      console.log('[SignalR] Received ScoutGenerationMessage:', message)
      try {
        const result: ShotGeneration = JSON.parse(message) as ShotGeneration

        queryClient.invalidateQueries({ queryKey: ['shots', result.CommercialId] })
        queryClient.invalidateQueries({ queryKey: ['shots', 'detail', result.ShotId] })
        // ShotGeneration does not carry the variantId, so invalidate all variant-scoped
        // queries for this scout — TanStack matches any key starting with the prefix.
        queryClient.invalidateQueries({ queryKey: ['shots', 'variant', result.ShotId] })
        queryClient.invalidateQueries({ queryKey: ['shots', 'gen-status', result.ShotId] })
        queryClient.invalidateQueries({ queryKey: ['queue'] })

        if (result.Status === GenerationStatus.Completed) {
          const onCommercialPage =
            location.pathname.startsWith('/commercial') &&
            location.pathname.includes(result.CommercialId)

          if (onCommercialPage) {
            showToast(`Shot generation completed`, 'success')
          } else {
            showToast(
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span>Shot generation completed</span>
                <Button
                  size="small"
                  shape="circular"
                  onClick={() =>
                    navigate(`/storyboard/commercial/${result.CommercialId}`, {
                      state: { keepStep: true, targetStep: StoryboardStep.ShotList },
                    })
                  }
                >
                  View result
                </Button>
              </div>,
              'success',
            )
          }
        } else {
          showToast(`Error generating shot image`, 'error')
        }
      } catch (error) {
        console.error('Error processing scout generation result:', error)
      }
    },
    [navigate],
  )

  const handleUserImageGenerationMessage = useCallback(
    (message: string) => {
      console.log('[SignalR] Received UserImageGenerationMessage:', message)
      try {
        const result: UserImageGeneration = JSON.parse(message) as UserImageGeneration

        queryClient.invalidateQueries({ queryKey: ['user-images'] })
        queryClient.invalidateQueries({ queryKey: ['user-images', 'detail', result.UserImageId] })
        queryClient.invalidateQueries({
          queryKey: ['user-images', 'gen-status', result.UserImageId],
        })
        queryClient.invalidateQueries({ queryKey: ['queue'] })

        if (result.Status === GenerationStatus.Completed) {
          const onUserImagePage =
            location.pathname.startsWith('/user-image') &&
            location.pathname.includes(result.UserImageId)

          if (onUserImagePage) {
            showToast(`Image generation completed`, 'success')
          } else {
            showToast(
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span>Image generation completed</span>
                <Button
                  size="small"
                  shape="circular"
                  onClick={() => navigate(`/user-image/${result.UserImageId}`)}
                >
                  View result
                </Button>
              </div>,
              'success',
            )
          }
        } else {
          showToast(`Image generation failed`, 'error')
        }
      } catch (error) {
        console.error('Error processing user image generation result:', error)
      }
    },
    [navigate],
  )

  const handleUpscaleMessage = useCallback(
    (message: string) => {
      console.log('[SignalR] Received UpscaleMessage:', message)
      try {
        const result: UpscaleGeneration = JSON.parse(message) as UpscaleGeneration

        queryClient.invalidateQueries({ queryKey: ['storyboard', result.CommercialId] })
        queryClient.invalidateQueries({
          queryKey: ['storyboard', 'upscale-status', result.CommercialId],
        })
        queryClient.invalidateQueries({ queryKey: ['queue'] })

        if (result.Status === GenerationStatus.Completed) {
          const onCommercialPage =
            location.pathname.startsWith('/commercial') &&
            location.pathname.includes(result.CommercialId)

          if (onCommercialPage) {
            showToast(`Storyboard upscaling completed`, 'success')
          } else {
            showToast(
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span>Storyboard upscaling completed</span>
                <Button
                  size="small"
                  shape="circular"
                  onClick={() =>
                    navigate(`/storyboard/commercial/${result.CommercialId}`, {
                      state: { keepStep: true, targetStep: StoryboardStep.Storyboard },
                    })
                  }
                >
                  View result
                </Button>
              </div>,
              'success',
            )
          }
        } else {
          showToast(`Error upscaling storyboard`, 'error')
        }
      } catch (error) {
        console.error('Error processing upscale result:', error)
      }
    },
    [navigate],
  )

  // ── Connection management ──────────────────────────────────────────

  const createConnection = useCallback(
    async (token: string) => {
      console.log('[SignalR] Creating new connection...')
      const hubUrl = import.meta.env.VITE_SIGNALR_HUB_URL as string

      const newConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: async () => {
            try {
              const freshToken = await acquireAccessToken()
              return freshToken || token
            } catch {
              return token
            }
          },
          transport:
            HttpTransportType.WebSockets |
            HttpTransportType.ServerSentEvents |
            HttpTransportType.LongPolling,
          skipNegotiation: false,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (isAuthenticationError) return null
            return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000)
          },
        })
        .configureLogging(LogLevel.Information)
        .build()

      // Register message handlers — these invalidate queries, not emit events
      newConnection.on('ReceiveGenerationMessage', handleGenerationMessage)
      newConnection.on('ReceiveScoutGenerationMessage', handleScoutGenerationMessage)
      newConnection.on('ReceiveUserImageGenerationMessage', handleUserImageGenerationMessage)
      newConnection.on('ReceiveUpscaleMessage', handleUpscaleMessage)
      newConnection.on('ReceiveImageStyleMessage', (message: string) => {
        try {
          handleImageStyleNotification(queryClient, message)
        } catch (error) {
          console.error('Error processing image style result', error)
          showToast(
            'Could not refresh the image style result. Check activities for details.',
            'error',
          )
        }
      })
      newConnection.on('ReceivePlaygroundImageGenerationMessage', (message: string) => {
        try {
          handlePlaygroundNotification(queryClient, message)
        } catch (error) {
          console.error('Error processing playground generation result', error)
          showToast(
            'Could not refresh the playground notification. Refresh the playground to check the result.',
            'error',
          )
        }
      })
      newConnection.on('ReceivePlaygroundVideoGenerationMessage', (message: unknown) => {
        try {
          const result = handlePlaygroundVideoNotification(queryClient, message)
          const terminal = !['Pending', 'Running'].includes(result.status)
          if (terminal && !notifiedVideoActivities.has(result.activityId)) {
            notifiedVideoActivities.add(result.activityId)
            if (result.status === 'Completed') {
              showToast('Video generation completed', 'success')
            } else if (result.status === 'Cancelled') {
              showToast('Video generation cancelled', 'info')
            } else {
              showToast(
                result.error ?? `Video generation finished with status ${result.status}`,
                'error',
              )
            }
          }
        } catch (error) {
          console.error('Error processing video playground generation result', error)
          showToast(
            'Could not refresh the video playground notification. Refresh to check the result.',
            'error',
          )
        }
      })

      newConnection.onclose((error) => {
        console.log('[SignalR] Connection closed. Error:', error)
        setIsConnected(false)
        setConnectionState('Disconnected')
        if (error) {
          const isAuthError =
            error.message.includes('401') ||
            error.message.includes('Unauthorized') ||
            error.message.includes('authentication')
          if (isAuthError) {
            setIsAuthenticationError(true)
            setAuthFailureCount((prev) => prev + 1)
            setLastAuthFailure(Date.now())
          }
        }
      })

      newConnection.onreconnecting(() => {
        console.log('[SignalR] Connection reconnecting...')
        setIsConnected(false)
        setConnectionState('Reconnecting')
      })

      newConnection.onreconnected(() => {
        console.log('[SignalR] Connection reconnected.')
        setIsConnected(true)
        setConnectionState('Connected')
        setIsAuthenticationError(false)
        setAuthFailureCount(0)
        void queryClient.invalidateQueries({ queryKey: playgroundKeys.all })
      })

      console.log('[SignalR] Starting connection...')
      await newConnection.start()
      console.log('[SignalR] Connection started successfully.')

      connectionRef.current = newConnection
      setConnection(newConnection)
      setIsConnected(true)
      setConnectionState('Connected')
      void queryClient.invalidateQueries({ queryKey: playgroundKeys.all })

      return newConnection
    },
    [
      handleGenerationMessage,
      handleScoutGenerationMessage,
      handleUserImageGenerationMessage,
      handleUpscaleMessage,
      isAuthenticationError,
    ],
  )

  const reconnect = useCallback(async () => {
    if (connectionRef.current?.state === HubConnectionState.Connected || isConnecting) return

    if (isAuthenticationError && authFailureCount >= 3 && Date.now() - lastAuthFailure < 60000)
      return

    try {
      console.log('[SignalR] Attempting manual reconnect...')
      setIsConnecting(true)
      setConnectionState('Connecting')
      if (connectionRef.current) {
        await connectionRef.current.stop()
        connectionRef.current = null
        setConnection(null)
      }
      const token = await acquireAccessToken()
      if (token && account) {
        setIsAuthenticationError(false)
        await createConnection(token)
      } else {
        setIsAuthenticationError(true)
        setAuthFailureCount((prev) => prev + 1)
        setLastAuthFailure(Date.now())
      }
    } catch {
      // Reconnect failure handled by automatic retry
    } finally {
      setIsConnecting(false)
    }
  }, [
    account,
    createConnection,
    isConnecting,
    isAuthenticationError,
    authFailureCount,
    lastAuthFailure,
  ])

  // Initial connection setup
  useEffect(() => {
    let currentConnection: HubConnection | null = null
    let tokenRefreshInterval: ReturnType<typeof setInterval> | null = null

    const setup = async () => {
      if (!account || connectionRef.current || isConnecting) return
      console.log('[SignalR] Initializing SignalR setup for account...')

      const token = await acquireAccessToken()
      setIsConnecting(true)
      try {
        if (!token) {
          console.warn('[SignalR] No access token available, aborting setup.')
          setIsConnecting(false)
          return
        }
        currentConnection = await createConnection(token)
        tokenRefreshInterval = setInterval(
          async () => {
            if (currentConnection?.state === HubConnectionState.Connected) {
              const freshToken = await acquireAccessToken()
              if (
                !freshToken &&
                (!isAuthenticationError || Date.now() - lastAuthFailure > 300000)
              ) {
                await reconnect()
              }
            }
          },
          45 * 60 * 1000,
        )
      } catch (error) {
        console.error('[SignalR] Connection error during setup:', error)
      } finally {
        setIsConnecting(false)
      }
    }

    setup()

    return () => {
      currentConnection?.stop()
      if (tokenRefreshInterval) clearInterval(tokenRefreshInterval)
    }
  }, [account, createConnection, isConnecting, reconnect, isAuthenticationError, lastAuthFailure])

  return (
    <SignalRContext.Provider
      value={{ connection, isConnected, isConnecting, connectionState, reconnect }}
    >
      {children}
    </SignalRContext.Provider>
  )
}

export const useSignalRContext = (): SignalRContextType => {
  const context = useContext(SignalRContext)
  if (!context) throw new Error('useSignalRContext must be used within a SignalRProvider')
  return context
}
